<template>
  <div id="cadastro">
    <button
      id="voltar"
      @click="$emit('fecharEscolherParticipantes')"
    >
      voltar
    </button>

    <form class="form">
      <h4>Escolher Participantes</h4>

      <div id="participantes" v-if="participantesAtividade.length > 0">
        <div
          v-for="participante in participantesAtividade"
          :key="participante.participanteId || participante.id"
          class="checkbox"
          :class="{ checked: participante.checked }"
        >
          <div class="participante-row-info">
            <input
              type="checkbox"
              :id="`participante-${participante.participanteId || participante.id}`"
              :value="participante.participanteId || participante.id"
              v-model="participante.checked"
            />
            <label :for="`participante-${participante.participanteId || participante.id}`">
              <span class="participante-nome">{{ participante.nome }}</span>
              <span class="participante-email" v-if="participante.email">{{ participante.email }}</span>
            </label>
          </div>
          <button
            type="button"
            class="btn-excluir-item"
            @click.stop="abrirModalExclusao(participante)"
            title="Excluir participante"
          >
            <i class="ti ti-trash"></i>
          </button>
        </div>
      </div>

      <div v-else class="sem-participantes">
        <p>Nenhum participante disponível para seleção.</p>
      </div>

      <div id="botoes">
        <button
          type="submit"
          id="enviar"
          @click.prevent="enviarParticipantes()"
          :disabled="enviando"
        >
          {{ enviando ? "Enviando..." : "Enviar" }}
        </button>
      </div>
    </form>

    <!-- Modal de confirmação de exclusão -->
    <div
      v-if="modalExclusaoVisivel"
      class="overlay-confirmacao"
      @click.self="fecharModalExclusao"
    >
      <div class="modal-confirmacao" @click.stop>
        <div class="confirm-icone confirm-vermelho">
          <i class="ti ti-alert-triangle"></i>
        </div>
        <h5 class="confirm-titulo">Excluir participante?</h5>
        <p class="confirm-msg">
          Tem certeza que deseja excluir <strong>{{ participanteParaExcluir?.nome }}</strong>?
          Esta ação não pode ser desfeita.
        </p>
        <div class="confirm-acoes">
          <button
            type="button"
            class="btn-confirm-cancelar"
            @click="fecharModalExclusao"
            :disabled="processandoExclusao"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn-confirm-ok btn-confirm-vermelho"
            @click="confirmarExclusao"
            :disabled="processandoExclusao"
          >
            {{ processandoExclusao ? "Excluindo..." : "Excluir" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import atividadeService from "@/service/atividadeService";
import participantesAtividadeService from "@/service/participantesAtividadeService";
import { useToast } from "@/composables/useToast";

const props = defineProps({
  atividadeId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["fecharEscolherParticipantes", "atualizar"]);
const toast = useToast();

const participantesAtividade = ref([]);
const enviando = ref(false);
const modalExclusaoVisivel = ref(false);
const participanteParaExcluir = ref(null);
const processandoExclusao = ref(false);

function abrirModalExclusao(participante) {
  participanteParaExcluir.value = participante;
  modalExclusaoVisivel.value = true;
}

function fecharModalExclusao() {
  participanteParaExcluir.value = null;
  modalExclusaoVisivel.value = false;
}

async function confirmarExclusao() {
  if (!participanteParaExcluir.value) return;
  const participanteId =
    participanteParaExcluir.value.participanteId ||
    participanteParaExcluir.value.id ||
    participanteParaExcluir.value.usuarioId;

  if (!participanteId) {
    toast.erro("Identificador do participante não encontrado.");
    fecharModalExclusao();
    return;
  }

  processandoExclusao.value = true;
  try {
    await participantesAtividadeService.removerInscricao(participanteId);
    participantesAtividade.value = participantesAtividade.value.filter(
      (p) => (p.participanteId || p.id || p.usuarioId) !== participanteId
    );
    toast.sucesso(
      `Participante ${participanteParaExcluir.value.nome} excluído com sucesso.`
    );
    fecharModalExclusao();
    emit("atualizar");
  } catch (error) {
    console.error("Erro ao excluir participante:", error);
    toast.erro(
      error?.response?.data?.message || "Erro ao excluir participante."
    );
  } finally {
    processandoExclusao.value = false;
  }
}

async function enviarParticipantes() {
  let participantesEscolhidos = [];
  participantesAtividade.value.forEach((participante) => {
    if (participante.checked) {
      participantesEscolhidos.push({
        email: participante.email,
        nome: participante.nome,
      });
    }
  });

  if (participantesEscolhidos.length === 0) {
    toast.alerta("Selecione pelo menos um participante antes de enviar.");
    return;
  }

  enviando.value = true;
  try {
    await atividadeService.escolherParticipantesAtividade(
      props.atividadeId,
      participantesEscolhidos
    );
    toast.sucesso("Participantes selecionados com sucesso!");
    emit("atualizar");
    emit("fecharEscolherParticipantes");
  } catch (error) {
    console.error("Erro ao enviar participantes escolhidos:", error);
    toast.erro(
      error?.response?.data?.message || "Erro ao selecionar participantes."
    );
  } finally {
    enviando.value = false;
  }
}

onMounted(async () => {
  try {
    const res = await participantesAtividadeService.getParticipantesAtividade(
      0,
      50,
      props.atividadeId
    );
    participantesAtividade.value = Array.isArray(res) ? res : res?.content || [];
  } catch (error) {
    console.error("Erro ao carregar participantes:", error);
    toast.erro("Erro ao carregar lista de participantes.");
  }
});
</script>

<style scoped>
#participantes {
  margin-top: 16px;
  width: 85%;
  max-height: 45vh;
  min-height: 15vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1.5px solid hsl(220, 15%, 88%);
  padding: 8px 12px;
  border-radius: 8px;
  background-color: #fff;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.checkbox:hover {
  border-color: hsl(262, 52%, 60%);
}

.checked {
  background-color: hsl(262, 70%, 96%);
  border-color: hsl(262, 60%, 55%);
}

.participante-row-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.participante-row-info input[type="checkbox"] {
  cursor: pointer;
  width: 16px;
  height: 16px;
  accent-color: hsl(262, 60%, 50%);
}

.participante-row-info label {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.participante-nome {
  font-size: 14px;
  font-weight: 500;
  color: hsl(220, 20%, 15%);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.participante-email {
  font-size: 12px;
  color: hsl(220, 10%, 50%);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn-excluir-item {
  background: transparent;
  border: none;
  color: hsl(0, 70%, 55%);
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-excluir-item:hover {
  background-color: hsl(0, 80%, 95%);
  color: hsl(0, 75%, 45%);
}

.sem-participantes {
  margin-top: 16px;
  padding: 16px;
  color: hsl(220, 10%, 50%);
  font-size: 14px;
}

#botoes {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

/* Modal de Confirmação */
.overlay-confirmacao {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.modal-confirmacao {
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.confirm-icone {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.confirm-vermelho {
  background: hsl(0, 85%, 95%);
  color: hsl(0, 72%, 51%);
}

.confirm-titulo {
  font-size: 18px;
  font-weight: 600;
  color: hsl(220, 20%, 15%);
  margin: 0;
}

.confirm-msg {
  font-size: 14px;
  color: hsl(220, 10%, 40%);
  margin: 0;
  line-height: 1.5;
}

.confirm-acoes {
  display: flex;
  gap: 12px;
  width: 100%;
  margin-top: 8px;
}

.btn-confirm-cancelar,
.btn-confirm-ok {
  flex: 1;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-confirm-cancelar {
  background: hsl(220, 15%, 92%);
  color: hsl(220, 15%, 25%);
}

.btn-confirm-cancelar:hover {
  background: hsl(220, 15%, 85%);
}

.btn-confirm-vermelho {
  background: hsl(0, 72%, 51%);
  color: #ffffff;
}

.btn-confirm-vermelho:hover {
  background: hsl(0, 72%, 43%);
}
</style>
